/**
 * localStorage utilities for warehouse layout persistence
 */

import {
  WarehouseLayout,
  LayoutStorageData,
  DEFAULT_WAREHOUSE_DIMENSIONS,
  DEFAULT_GRID_UNIT
} from '../types/warehouseLayout';

const STORAGE_KEY = 'bizclick-warehouse-layouts';
const STORAGE_VERSION = 1;

/**
 * Get all stored layouts from localStorage
 */
export function getAllLayouts(): Record<string, WarehouseLayout> {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return {};

    const parsed: LayoutStorageData = JSON.parse(data);
    return parsed.layouts || {};
  } catch (error) {
    console.error('Error reading layouts from localStorage:', error);
    return {};
  }
}

/**
 * Get layout for a specific warehouse
 */
export function getLayout(warehouseId: string): WarehouseLayout | null {
  const layouts = getAllLayouts();
  return layouts[warehouseId] || null;
}

/**
 * Save layout to localStorage
 */
export function saveLayout(layout: WarehouseLayout): void {
  try {
    const layouts = getAllLayouts();
    layouts[layout.warehouseId] = {
      ...layout,
      lastModified: new Date().toISOString(),
      version: (layout.version || 0) + 1
    };

    const storageData: LayoutStorageData = {
      layouts,
      lastAccessed: new Date().toISOString(),
      version: STORAGE_VERSION
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
  } catch (error) {
    console.error('Error saving layout to localStorage:', error);
    throw new Error('Kunne ikke lagre layout');
  }
}

/**
 * Delete layout from localStorage
 */
export function deleteLayout(warehouseId: string): void {
  try {
    const layouts = getAllLayouts();
    delete layouts[warehouseId];

    const storageData: LayoutStorageData = {
      layouts,
      lastAccessed: new Date().toISOString(),
      version: STORAGE_VERSION
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
  } catch (error) {
    console.error('Error deleting layout from localStorage:', error);
  }
}

/**
 * Create a new empty layout for a warehouse
 */
export function createEmptyLayout(warehouseId: string, name?: string): WarehouseLayout {
  return {
    warehouseId,
    name: name || `Lager ${warehouseId}`,
    dimensions: { ...DEFAULT_WAREHOUSE_DIMENSIONS },
    gridUnit: DEFAULT_GRID_UNIT,
    racks: [],
    objects: [],
    version: 1,
    lastModified: new Date().toISOString()
  };
}

/**
 * Export layout as JSON file
 */
export function exportLayoutAsJson(layout: WarehouseLayout): void {
  const json = JSON.stringify(layout, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `warehouse-layout-${layout.warehouseId}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Import layout from JSON file
 */
export function importLayoutFromJson(file: File): Promise<WarehouseLayout> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const layout = JSON.parse(content) as WarehouseLayout;

        // Validate required fields
        if (!layout.warehouseId || !layout.dimensions || !Array.isArray(layout.racks)) {
          throw new Error('Ugyldig layout-fil: mangler påkrevde felt');
        }

        resolve(layout);
      } catch (error) {
        reject(new Error('Kunne ikke lese layout-fil: ugyldig format'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Kunne ikke lese fil'));
    };

    reader.readAsText(file);
  });
}

/**
 * Check if localStorage is available
 */
export function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get storage usage info
 */
export function getStorageInfo(): { used: number; available: boolean } {
  const data = localStorage.getItem(STORAGE_KEY);
  return {
    used: data ? new Blob([data]).size : 0,
    available: isStorageAvailable()
  };
}
